import React, { useMemo } from 'react';

import Suspense from '@atlaskit/react-ufo/suspense';
import { History } from 'history';

import { checkUserHasCapabilitiesFromVolt } from '@townsquare/capability-provider/checkUserHasCapabilities';
import { type CollaborationContext } from '@townsquare/collab-context';
import { AGG_GOAL_PAGE, GOAL_PAGE } from '@townsquare/config/routes';
import { filterFalsyStrings, filterNull } from '@townsquare/filter-type';
import type { MatchedRoute } from '@townsquare/router/routing-types';
import { userAccessRedirectContextHolder } from '@townsquare/router/user-access';
import { createSearchParamsFromCurrentUrl } from '@townsquare/search-params';
import { User } from '@townsquare/user-store/types';
import { useWorkspaceActions } from '@townsquare/workspace-store/actions';
import { WorkspaceDetails } from '@townsquare/workspace-store/types';

import { routes } from '../router/routes';
import { FullScreenSpaLoader } from '../ui/FullScreenSpaLoader';

import { BootstrapShell, type BootstrapShellData } from './BootstrapShell';
import { BootstrapErrorBoundary } from './ErrorBoundary/BootstrapErrorBoundary';
import { NotFoundScreen, RestrictedGoalForbiddenScreen } from './ErrorBoundary/scenarios';
import { deriveContextCloudId } from './deriveBootstrap';
import { type BootstrapVoltPayload, type BootstrapVoltWorkspace } from './voltPayload';

export type BootstrapFromVoltResult = {
	orgId?: string;
	cloudIds: string[];
	workspaceIds: string[];
	workspaceUuids: string[];
	workspace?: WorkspaceDetails;
	user: User;
	workspaceVolt: BootstrapVoltWorkspace | undefined;
	collabContext?: CollaborationContext | null;
};

type BootstrapFromVoltProps = {
	children: (data: BootstrapFromVoltResult) => JSX.Element;
	matchedRoute: MatchedRoute | null;
	history: History<unknown>;
	payload: BootstrapVoltPayload;
};

/**
 * Volt branch of the goals bootstrap — reads the server-inlined payload,
 * derives the same application-level data as `Bootstrap.tsx`, and hands
 * the result to `BootstrapShell` for all branch-agnostic side-effects.
 *
 * Structurally mirrors `Bootstrap.tsx`; the key difference is the data source
 * (inline window payload vs Relay preloaded query) and the absence of
 * `usePreloadedQuery` / Relay fragment refs. Uses `CapabilityRouterFromVolt`
 * in App.tsx (raw capabilities from the payload instead of a fragment ref).
 *
 * Deleted when `Bootstrap.tsx` is deleted (Volt at 100% on goals routes).
 */
function BootstrapFromVoltResolution(props: BootstrapFromVoltProps): JSX.Element {
	const { payload, matchedRoute, history } = props;

	const orgId = payload.orgId ?? undefined;
	// Memoised so referential equality holds — the shell uses these as useMemo
	// deps and the window global is itself stable. Master had `?? null`; preserve.
	const orgCloudIds = useMemo(
		() => payload.cloudIds?.filter(filterFalsyStrings) ?? null,
		[payload.cloudIds],
	);
	const cloudIds = useMemo(() => orgCloudIds ?? [], [orgCloudIds]);
	const workspaces = useMemo(
		() => payload.workspaces?.filter(filterNull) ?? [],
		[payload.workspaces],
	);
	const workspaceIds = useMemo(
		() => workspaces.map((w) => w.id).filter(filterFalsyStrings),
		[workspaces],
	);
	const workspaceUuids = useMemo(
		() => workspaces.map((w) => w.uuid).filter(filterFalsyStrings),
		[workspaces],
	);

	const siteIdentifier = matchedRoute?.match.params.siteIdentifier;
	const contextCloudId = useMemo(
		() =>
			deriveContextCloudId(
				siteIdentifier || createSearchParamsFromCurrentUrl().get('cloudId') || undefined,
				cloudIds,
			),
		[siteIdentifier, cloudIds],
	);

	const workspace = useMemo(
		() =>
			workspaces
				.filter((w) => w.type === 'GLOBAL_EXPERIENCE')
				.find((w) => w.cloudId === contextCloudId),
		[workspaces, contextCloudId],
	);

	const workspaceActions = useWorkspaceActions();
	let workspaceDetails: WorkspaceDetails | undefined;
	if (workspace && orgId) {
		workspaceDetails = workspaceActions.initWorkspaceFromHomeFromVolt(workspace, {
			numWorkspaces: workspaceIds.length,
			userIsOrgAdmin: payload.userIsOrgAdmin ?? false,
			organisationId: orgId,
		});
	}

	const userHasGoalAccess = workspace
		? checkUserHasCapabilitiesFromVolt(workspace, ['goals_app:access'], true)
		: false;

	const collabContextSource = useMemo(
		() =>
			payload.collabContext
				? {
						cloudIds: payload.collabContext.cloudIds?.filter(filterFalsyStrings) ?? [],
						isMultiSite: payload.collabContext.isMultiSite ?? false,
						hasMultipleUnits: payload.collabContext.hasMultipleUnits ?? false,
					}
				: null,
		[payload.collabContext],
	);

	const shellData: BootstrapShellData = {
		orgId,
		cloudIds,
		orgCloudIds,
		workspaceIds,
		workspaceUuids,
		contextCloudId,
		collabContextSource,
		activeWorkspace:
			workspace && workspace.cloudId && workspace.uuid
				? { cloudId: workspace.cloudId, uuid: workspace.uuid }
				: undefined,
		userHasGoalAccess,
		userIsOrgAdmin: payload.userIsOrgAdmin ?? false,
	};

	return (
		<BootstrapShell
			bootstrap={shellData}
			workspaceDetails={workspaceDetails}
			matchedRoute={matchedRoute}
			history={history}
		>
			{({ collabContext, currentUser }) =>
				props.children({
					orgId,
					cloudIds,
					workspaceIds,
					workspaceUuids,
					workspace: workspaceDetails,
					user: currentUser,
					workspaceVolt: workspace,
					collabContext,
				})
			}
		</BootstrapShell>
	);
}

export function BootstrapFromVolt(props: BootstrapFromVoltProps): React.JSX.Element {
	const isGoalDetailsRoute =
		props.matchedRoute?.route.name === GOAL_PAGE ||
		props.matchedRoute?.route.name === AGG_GOAL_PAGE;

	// Must run before <Suspense>: BootstrapErrorBoundary reads it when the populator throws.
	userAccessRedirectContextHolder.initUserAccessRedirect(routes);

	if (props.matchedRoute?.route.name === '404-page') {
		return <NotFoundScreen />;
	}

	return (
		<BootstrapErrorBoundary
			pageErrorFallbacks={
				isGoalDetailsRoute ? { forbidden: <RestrictedGoalForbiddenScreen /> } : undefined
			}
		>
			<Suspense interactionName="spa-loader" fallback={<FullScreenSpaLoader />}>
				<BootstrapFromVoltResolution {...props} />
			</Suspense>
		</BootstrapErrorBoundary>
	);
}
